import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { interactionsAPI } from '../../services/api';

const INTERACTION_ICONS: Record<string, string> = {
  call: '📞',
  email: '📧',
  sms: '💬',
  meeting: '🤝',
  note: '📝',
  task: '✅',
};

const INTERACTION_LABELS: Record<string, string> = {
  call: 'Call',
  email: 'Email',
  sms: 'Text',
  meeting: 'Meeting',
  note: 'Note',
  task: 'Task',
};

export default function ActivityFeed() {
  const { data, isLoading } = useQuery({
    queryKey: ['recent-activity'],
    queryFn: async () => {
      const result = await interactionsAPI.list({ limit: 20 });
      return result.data?.interactions || [];
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const activities = data || [];

  const formatRelativeTime = (timestamp: number) => {
    const now = Math.floor(Date.now() / 1000);
    const diff = now - timestamp;

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">No recent activity. Start logging interactions!</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
      
      <div className="space-y-3">
        {activities.map((activity: any) => {
          const icon = INTERACTION_ICONS[activity.type] || '📝';
          const label = INTERACTION_LABELS[activity.type] || activity.type;
          const contactName = `${activity.first_name} ${activity.last_name || ''}`.trim();

          return (
            <Link
              key={activity.id}
              to={`/contacts/${activity.contact_id}`}
              className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
            >
              <span className="text-2xl flex-shrink-0">{icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-ondeck-600 dark:group-hover:text-ondeck-400">
                    {label} with {contactName}
                  </p>
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                    {formatRelativeTime(activity.occurred_at)}
                  </span>
                </div>
                
                {activity.subject && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-1 truncate">
                    {activity.subject}
                  </p>
                )}
                
                {activity.snippet && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                    {activity.snippet}
                  </p>
                )}
                
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    activity.direction === 'outbound'
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                  }`}>
                    {activity.direction === 'outbound' ? 'Outbound' : 'Inbound'}
                  </span>
                  {activity.channel && activity.channel !== 'manual' && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      via {activity.channel}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
